// Agendamiento · Servicios
//
// Qué se vende (quiropraxia, masaje…), cuánto dura, cuánto cuesta y las reglas de
// reserva. Cambiar el precio o la duración NO afecta las reservas ya pagadas: cada
// reserva guarda lo que se cobró.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Dropdown, Form, Input, InputNumber, Modal, Select, Switch, Table } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { FiActivity, FiCheckCircle, FiInfo, FiLayers, FiMoreVertical, FiPauseCircle, FiPlus, FiSearch } from 'react-icons/fi';
import Agenda from '../../../../service/Agenda';
import Recintos from '../../../../service/Recintos';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import { formatearNombre } from '../../../../utils/texto';
import {
  EncabezadoAdmin, TarjetaResumen, ChipEstado, ChipNeutro, claseBtnPrimario, claseBtnSecundario,
} from '../../../Ui/Admin/PiezasAdmin';
import { BarraFiltros, Vacio, claseBtnKebab } from './piezas';
import { pesos } from './util';

// La duración es la de la atención y va de 5 en 5 (un masaje dura 50). En la agenda
// ocupa bloques de 15: 50 min ocupa 60. «Horas cada» sí va de 15 en 15.
const BLOQUE_MIN = 15;
const ocupacion = (d) => Math.ceil(d / BLOQUE_MIN) * BLOQUE_MIN;
const DURACIONES = Array.from({ length: 47 }, (_, i) => 10 + i * 5).map(m => ({ value: m, label: `${m} min` }));
const PASOS = Array.from({ length: 16 }, (_, i) => (i + 1) * BLOQUE_MIN).map(m => ({ value: m, label: `${m} min` }));

const propsPesos = {
  className: 'w-full', min: 1, step: 500,
  formatter: (v) => (v ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''),
  parser: (v) => v.replace(/\$\s?|\./g, ''),
};

/** Precio vecino / adulto mayor: opcional, y nunca mayor que el normal. */
const reglaRebaja = (etiqueta) => ({ getFieldValue }) => ({
  validator(_, v) {
    const normal = getFieldValue('precio');
    if (v == null || !normal || v <= normal) return Promise.resolve();
    return Promise.reject(new Error(`${etiqueta} no puede ser mayor que el normal`));
  },
});

const VACIO = {
  activo: true, tieneIVA: false, anticipacionMinHoras: 2, ventanaDias: 30,
  reprogramacionMinHoras: 24, maxReprogramaciones: 1, orden: 0,
};

/**
 * Crear o editar un servicio. Es un componente aparte a propósito: se monta en cada
 * apertura, así cada vez parte con su propio formulario y sus valores iniciales (con
 * un único useForm compartido, reabrir mostraba los datos del servicio anterior).
 */
function ModalServicio({ servicio, recintos, onCerrar, onGuardado }) {
  const notify = useNotify();
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const duracion = Form.useWatch('duracionMin', form);
  const valoresIniciales = servicio._id ? { ...servicio, recinto: servicio.recinto?._id || servicio.recinto } : VACIO;

  const guardar = async (valores) => {
    setGuardando(true);
    try {
      const datos = {
        ...valores, pasoMin: valores.pasoMin || null, sku: valores.sku?.trim() || null,
        precioVecino: valores.precioVecino || null, precioAdultoMayor: valores.precioAdultoMayor || null,
      };
      if (servicio._id) await Agenda.actualizarServicio(servicio._id, datos);
      else await Agenda.crearServicio(datos);
      notify.success(servicio._id ? 'Servicio actualizado' : 'Servicio creado');
      onGuardado();
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo guardar el servicio'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open onCancel={onCerrar} width={720} destroyOnHidden
      title={servicio?._id ? <>Editar <span className="hl-amarillo">{servicio.nombre}</span></> : 'Nuevo servicio'}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className={claseBtnSecundario}>Cancelar</button>
          <button type="button" onClick={() => form.submit()} disabled={guardando} className={claseBtnPrimario}>
            {guardando && <LoadingOutlined />} {servicio._id ? 'Guardar cambios' : 'Crear servicio'}
          </button>
        </div>
      }
    >
      {servicio?._id && (
        <p className="text-xs text-gray-500 mb-4">Las reservas ya pagadas conservan el precio y la duración con que se compraron.</p>
      )}
      <Form form={form} layout="vertical" onFinish={guardar} autoComplete="off" className="form-compacto" initialValues={valoresIniciales}>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_260px] gap-x-4">
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true, message: 'Indica el nombre' }]}>
            <Input maxLength={120} placeholder="Quiropraxia" />
          </Form.Item>
          <Form.Item name="recinto" label="Recinto" rules={[{ required: true, message: 'Elige el recinto' }]}
            extra="Define la sucursal de Bsale y la dirección del correo.">
            <Select placeholder="Selecciona un recinto" showSearch optionFilterProp="label"
              options={recintos.map(r => ({ value: r._id, label: formatearNombre(r.nombre) }))} />
          </Form.Item>
        </div>
        <Form.Item name="descripcion" label="Descripción (se muestra en el sitio)">
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={2000} showCount />
        </Form.Item>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4">
          <Form.Item name="duracionMin" label="Duración" rules={[{ required: true, message: 'Elige la duración' }]}
            extra={duracion && duracion % BLOQUE_MIN ? `Ocupa ${ocupacion(duracion)} min en la agenda` : null}>
            <Select options={DURACIONES} showSearch />
          </Form.Item>
          <Form.Item name="pasoMin" label="Horas cada"
            tooltip="Cada cuántos minutos se ofrece una hora. Vacío = lo que ocupa en la agenda (50 min → cada 60).">
            <Select allowClear placeholder={duracion ? `= ${ocupacion(duracion)} min` : '= duración'} options={PASOS} />
          </Form.Item>
          <Form.Item name="tieneIVA" label="Boleta afecta" valuePropName="checked" tooltip="Lo define finanzas. Apagado = boleta exenta.">
            <Switch />
          </Form.Item>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
          <Form.Item name="precio" label="Precio normal (IVA incl.)" rules={[{ required: true, message: 'Indica el precio' }]}>
            <InputNumber {...propsPesos} placeholder="$ 25.000" />
          </Form.Item>
          <Form.Item name="precioVecino" label="Precio vecino" dependencies={['precio']} rules={[reglaRebaja('El precio vecino')]}
            tooltip="Con Tarjeta Vecino vigente: se valida con el RUT al reservar. Vacío = no hay precio vecino.">
            <InputNumber {...propsPesos} placeholder="Opcional" />
          </Form.Item>
          <Form.Item name="precioAdultoMayor" label="Precio adulto mayor (+60)" dependencies={['precio']} rules={[reglaRebaja('El precio adulto mayor')]}
            tooltip="Con 60 años o más según la fecha de nacimiento que declara la persona, tenga o no Tarjeta Vecino; se verifica la cédula en el recinto. Vacío = no hay precio adulto mayor.">
            <InputNumber {...propsPesos} placeholder="Opcional" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4">
          <Form.Item name="anticipacionMinHoras" label="Reservar desde (h)" tooltip="Horas mínimas de anticipación para reservar.">
            <InputNumber className="w-full" min={0} max={336} />
          </Form.Item>
          <Form.Item name="ventanaDias" label="Hasta (días)" tooltip="Cuántos días hacia adelante se puede reservar.">
            <InputNumber className="w-full" min={1} max={180} />
          </Form.Item>
          <Form.Item name="maxReprogramaciones" label="Cambios de hora">
            <InputNumber className="w-full" min={0} max={10} />
          </Form.Item>
          <Form.Item name="reprogramacionMinHoras" label="Cambio hasta (h)" tooltip="Horas antes de la cita en que todavía se puede cambiar.">
            <InputNumber className="w-full" min={0} max={336} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4">
          <Form.Item name="sku" label="SKU en Bsale (opcional)">
            <Input maxLength={60} />
          </Form.Item>
          <Form.Item name="orden" label="Orden en el sitio">
            <InputNumber className="w-full" min={0} />
          </Form.Item>
          <Form.Item name="activo" label="Activo" valuePropName="checked" className="!mb-0">
            <Switch />
          </Form.Item>
        </div>
      </Form>
      {servicio?._id && !servicio.activo && (
        <Alert type="warning" showIcon className="mt-2" message="Este servicio no se está ofreciendo en el sitio" />
      )}
    </Modal>
  );
}

export default function Servicios() {
  const notify = useNotify();
  const { user } = useAuthContext();
  const puedeEditar = can(user, 'agenda', 'editar');
  const [servicios, setServicios] = useState([]);
  const [recintos, setRecintos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [vista, setVista] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [editando, setEditando] = useState(null); // null | {} (nuevo) | servicio

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setServicios(await Agenda.servicios());
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudieron cargar los servicios'));
    } finally {
      setCargando(false);
    }
  }, [notify]);

  useEffect(() => {
    cargar();
    Recintos.List({ limit: 1000 }).then(r => setRecintos((r.data || []).filter(x => x.estado === 'activo'))).catch(() => {});
  }, [cargar]);

  const conteo = useMemo(() => ({
    todos: servicios.length,
    activos: servicios.filter(s => s.activo).length,
    inactivos: servicios.filter(s => !s.activo).length,
  }), [servicios]);

  const visibles = servicios
    .filter(s => vista === 'todos' || (vista === 'activos' ? s.activo : !s.activo))
    .filter(s => !busqueda || s.nombre.toLowerCase().includes(busqueda.toLowerCase()));

  const abrir = (s) => setEditando(s || {});
  const cerrar = () => setEditando(null);

  const cambiarEstado = async (s) => {
    try {
      await Agenda.actualizarServicio(s._id, { activo: !s.activo });
      notify.success(s.activo ? 'Servicio desactivado: deja de ofrecerse en el sitio' : 'Servicio activado');
      cargar();
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo cambiar el estado'));
    }
  };

  const columnas = [
    {
      title: 'Servicio', dataIndex: 'nombre', key: 'nombre',
      render: (n, s) => (
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
            ${s.activo ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-400'}`}>
            <FiActivity size={16} />
          </span>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 truncate">{n}</div>
            <div className="text-xs text-gray-500 truncate">{formatearNombre(s.recinto?.nombre) || 'Sin recinto'}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Duración', dataIndex: 'duracionMin', key: 'duracion', width: 120,
      render: (d, s) => <ChipNeutro>{d} min{s.pasoMin && s.pasoMin !== d ? ` · cada ${s.pasoMin}` : ''}</ChipNeutro>,
    },
    {
      title: 'Precio', dataIndex: 'precio', key: 'precio', width: 200,
      render: (p, s) => (
        <div>
          <span className="font-bold text-gray-900 tabular-nums">{pesos(p)}</span>
          {(s.precioVecino || s.precioAdultoMayor) && (
            <span className="block text-xs text-gray-600 tabular-nums">
              {[s.precioVecino && `Vecino ${pesos(s.precioVecino)}`, s.precioAdultoMayor && `+60 ${pesos(s.precioAdultoMayor)}`].filter(Boolean).join(' · ')}
            </span>
          )}
          <span className="block text-xs text-gray-500">Boleta {s.tieneIVA ? 'afecta' : 'exenta'}</span>
        </div>
      ),
    },
    {
      title: 'Reglas', key: 'reglas',
      render: (_, s) => (
        <span className="text-xs text-gray-600">
          Desde {s.anticipacionMinHoras} h antes · hasta {s.ventanaDias} días ·{' '}
          {s.maxReprogramaciones === 0 ? 'sin cambios de hora' : `${s.maxReprogramaciones} cambio(s) hasta ${s.reprogramacionMinHoras} h antes`}
        </span>
      ),
    },
    { title: 'Estado', dataIndex: 'activo', key: 'estado', width: 120, render: (a) => <ChipEstado activo={a} /> },
    ...(puedeEditar ? [{
      title: <span className="sr-only">Acciones</span>, key: 'acciones', width: 64, align: 'center',
      render: (_, s) => (
        <span onClick={e => e.stopPropagation()}>
          <Dropdown trigger={['click']} placement="bottomRight" menu={{
            items: [
              { key: 'editar', icon: <EditOutlined />, label: 'Editar', onClick: () => abrir(s) },
              {
                key: 'estado', icon: s.activo ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                label: s.activo ? 'Desactivar' : 'Activar', onClick: () => cambiarEstado(s),
              },
            ],
          }}>
            <button type="button" aria-label={`Acciones de ${s.nombre}`} className={claseBtnKebab}><FiMoreVertical size={17} /></button>
          </Dropdown>
        </span>
      ),
    }] : []),
  ];

  return (
    <div>
      <EncabezadoAdmin
        titulo="Servicios con"
        destacado="hora"
        subtitulo={`${conteo.todos} servicios · ${conteo.activos} activos · ${conteo.inactivos} inactivos`}
      >
        {puedeEditar && (
          <button type="button" onClick={() => abrir(null)} className={claseBtnPrimario}>
            <FiPlus size={16} /> Nuevo servicio
          </button>
        )}
      </EncabezadoAdmin>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <TarjetaResumen icon={<FiLayers size={19} />} label="Todos" value={conteo.todos} activa={vista === 'todos'} onClick={() => setVista('todos')} />
        <TarjetaResumen icon={<FiCheckCircle size={19} />} label="Activos" value={conteo.activos} activa={vista === 'activos'} onClick={() => setVista('activos')} />
        <TarjetaResumen icon={<FiPauseCircle size={19} />} label="Inactivos" value={conteo.inactivos} activa={vista === 'inactivos'} onClick={() => setVista('inactivos')} />
      </div>

      <BarraFiltros>
        <Input allowClear prefix={<FiSearch className="text-gray-400" />} placeholder="Buscar servicio…"
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="!rounded-full" style={{ maxWidth: 340 }} aria-label="Buscar servicio" />
        <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
          <FiInfo size={13} /> Aparece en el sitio si está activo y algún profesional o box lo atiende
        </span>
      </BarraFiltros>

      <div className="admin-table-card">
        <Table
          columns={columnas} dataSource={visibles} loading={cargando} rowKey="_id" sticky
          rowClassName={() => (puedeEditar ? 'fila-clicable' : '')}
          onRow={(s) => ({ onClick: () => puedeEditar && abrir(s) })}
          pagination={visibles.length > 20 ? { pageSize: 20, style: { padding: '12px 16px' } } : false}
          scroll={{ x: 900 }}
          locale={{
            emptyText: servicios.length
              ? <Vacio titulo="Ningún servicio coincide" texto="Prueba con otra búsqueda o filtro." />
              : <Vacio titulo="Aún no hay servicios" texto="Crea el primero para empezar a recibir reservas." />,
          }}
        />
      </div>

      <details className="mt-5 rounded-2xl bg-white border border-gray-100 p-4">
        <summary className="cursor-pointer text-sm font-bold text-gray-700 list-none flex items-center gap-2">
          <FiInfo size={15} className="text-primary" /> Cómo funcionan los servicios
        </summary>
        <ul className="mt-3 mb-0 pl-5 space-y-1.5 text-sm text-gray-600">
          <li>Las horas se ofrecen dentro del horario de cada profesional o box, cada «duración» minutos (o cada «horas cada», si se define). Una duración que no es múltiplo de 15 ocupa el bloque siguiente: un masaje de 50 min ocupa 60 y deja 10 libres entre personas.</li>
          <li>Precio vecino: se cobra si el RUT tiene Tarjeta Vecino vigente. Precio adulto mayor: si la persona declara 60 años o más, tenga o no Tarjeta Vecino (se verifica la cédula en el recinto). Si aplican ambos, se cobra el adulto mayor; no se suman.</li>
          <li>«Reservar desde» evita reservas de último minuto; «hasta» limita cuántos días hacia adelante se puede elegir.</li>
          <li>La persona puede cambiar su hora desde el link de su correo según «cambios de hora» y «cambio hasta».</li>
          <li>Boleta afecta o exenta lo define finanzas. Los cambios de precio o duración no afectan reservas ya pagadas.</li>
        </ul>
      </details>

      {editando !== null && (
        <ModalServicio servicio={editando} recintos={recintos} onCerrar={cerrar} onGuardado={() => { cerrar(); cargar(); }} />
      )}
    </div>
  );
}
